import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CrmClientProvider, createWebCrmClient } from './api/client.ts';
import { hydrateOutbox } from './lib/outbox.ts';
import { noteRestore, persistOptions } from './lib/query-persist.ts';
import { createAppQueryClient, createAppRouter } from './router.tsx';
import './styles.css';

const container = document.getElementById('root');
if (container === null) {
  throw new Error('#root is missing from index.html');
}

const queryClient = createAppQueryClient();
const crm = createWebCrmClient();
const router = createAppRouter({ queryClient, crm });

/**
 * Unsent mutations from a previous session, read off IndexedDB.
 *
 * Not awaited: the app must render on a machine where IndexedDB is blocked, and
 * blocking first paint on a storage read would trade a working page for a
 * queue nobody is looking at yet. `hydrateOutbox` notifies its subscribers when
 * it lands, so the surfaces that show the queue update themselves.
 */
void hydrateOutbox();

createRoot(container).render(
  <StrictMode>
    {/*
      `PersistQueryClientProvider` rather than `QueryClientProvider` (design
      §2.12): it restores the persisted cache BEFORE mounting children, so the
      first paint after a reload on a flaky connection is the cached data rather
      than a full page of skeletons that then get replaced.

      `onSuccess` fires once the restore has finished. `noteRestore` publishes
      the restored cache's own timestamp, which is what `CachedDataBanner` names
      — see `query-persist.ts` for why that timestamp cannot come from the
      library's own persisters, and why it is not inferred from the cache.
    */}
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions()}
      onSuccess={noteRestore}
    >
      <CrmClientProvider value={crm}>
        <RouterProvider router={router} />
      </CrmClientProvider>
    </PersistQueryClientProvider>
  </StrictMode>,
);
